// src/components/Layout.jsx
import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Tooltip, Drawer, Grid } from 'antd';
import {
  LaptopOutlined, ShoppingCartOutlined,
  SettingOutlined, TeamOutlined, FileTextOutlined,
  LogoutOutlined, UserOutlined, CloudOutlined,
  RollbackOutlined, MenuOutlined, BarChartOutlined,
} from '@ant-design/icons';

import { usePermission } from '../hooks/usePermission';

const { Content, Sider } = Layout;
const { useBreakpoint } = Grid;

const BRAND_PURPLE = '#6B21A8';
const BRAND_ORANGE = '#F97316';
const SIDEBAR_WIDTH = 220;

function Brand() {
  return (
    <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.12)', flexShrink: 0 }}>
      <div style={{ marginBottom: 6 }}>
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
          <path d="M14 2C14 2 8 8 8 14.5C8 18.09 10.69 21 14 21C17.31 21 20 18.09 20 14.5C20 12 18.5 10 17 8.5C17 8.5 16.5 11 15 12C15 12 16 9 14 2Z" fill={BRAND_ORANGE} />
          <path d="M14 21C11.5 21 10 18.5 10 16.5C10 15 10.8 13.8 12 13C12 13 11.8 14.5 13 15.5C13 15.5 13 14 14 13C14 13 15 15 14.5 16.5C14.5 16.5 16 15.5 16 14C16 14 17.5 15 17.5 16.5C17.5 18.5 16 21 14 21Z" fill="#FCD34D" />
        </svg>
      </div>
      <div style={{ color: '#fff', fontWeight: 800, fontSize: 15, letterSpacing: 1, lineHeight: 1.1 }}>
        SPORTSPIRED
      </div>
      <div style={{ color: BRAND_ORANGE, fontWeight: 600, fontSize: 11, letterSpacing: 2 }}>
        GAMES
      </div>
      <div style={{ marginTop: 8, color: 'rgba(255,255,255,0.4)', fontSize: 10, letterSpacing: 0.5 }}>
        IT ASSETS
      </div>
    </div>
  );
}

export default function AppLayout({ login, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const [menuOpen, setMenuOpen] = useState(false);

  // Keep the full fixed sidebar only when there is enough horizontal room.
  // Below 1200px it moves into a drawer so content keeps the whole viewport width.
  const desktopSidebar = screens.xl === true;

  const isAdmin = localStorage.getItem('is_admin') === 'true';
  const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';
  const { canView: canViewITAnalytics } = usePermission('it_analytics');

  const menuItems = [
    {
      type: 'group',
      label: <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, letterSpacing: 1 }}>IT АКТИВЫ</span>,
      children: [
        { key: '/equipment',   icon: <LaptopOutlined />,       label: 'Оборудование' },
        { key: '/employees',   icon: <TeamOutlined />,         label: 'Сотрудники' },
        { key: '/services',    icon: <CloudOutlined />,        label: 'Сервисы' },
        { key: '/consumables', icon: <ShoppingCartOutlined />, label: 'Расходники' },
        { key: '/acts',        icon: <FileTextOutlined />,     label: 'Акты выдачи' },
        { key: '/return-acts', icon: <RollbackOutlined />,     label: 'Акты возврата' },
        ...(canViewITAnalytics ? [
          { key: '/it-analytics', icon: <BarChartOutlined />, label: 'IT Аналитика' },
        ] : []),
      ],
    },
    ...((isAdmin || isSuperAdmin) ? [{
      type: 'group',
      label: <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, letterSpacing: 1 }}>СИСТЕМА</span>,
      children: [
        { key: '/admin', icon: <SettingOutlined />, label: 'Админка' },
      ],
    }] : []),
  ];

  const allKeys = [
    '/equipment', '/employees', '/services', '/consumables', '/acts', '/return-acts',
    '/it-analytics', '/admin',
  ];
  const selectedKey = allKeys.find(k => location.pathname.startsWith(k)) || '/equipment';

  const handleNavigate = (key) => {
    navigate(key);
    setMenuOpen(false);
  };

  const sidebarContent = (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: BRAND_PURPLE }}>
      <Brand />

      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        items={menuItems}
        onClick={({ key }) => handleNavigate(key)}
        style={{ background: 'transparent', borderRight: 'none', marginTop: 8, flex: 1 }}
        theme="dark"
      />

      <div style={{
        padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <UserOutlined style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, flexShrink: 0 }} />
          <span style={{
            color: 'rgba(255,255,255,0.6)', fontSize: 11,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {login}
          </span>
        </div>
        <Tooltip title="Выйти">
          <Button
            type="text" size="small"
            icon={<LogoutOutlined style={{ color: 'rgba(255,255,255,0.5)' }} />}
            onClick={() => { setMenuOpen(false); onLogout(); }}
          />
        </Tooltip>
      </div>
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {desktopSidebar ? (
        <Sider width={SIDEBAR_WIDTH} style={{
          background: BRAND_PURPLE, borderRight: 'none',
          position: 'fixed', height: '100vh', left: 0, top: 0, zIndex: 100,
          overflowY: 'auto', overflowX: 'hidden',
        }}>
          {sidebarContent}
        </Sider>
      ) : (
        <>
          <div className="mobile-app-header">
            <Button
              type="text"
              aria-label="Открыть меню"
              icon={<MenuOutlined style={{ fontSize: 20, color: BRAND_PURPLE }} />}
              onClick={() => setMenuOpen(true)}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ color: BRAND_PURPLE, fontWeight: 800, fontSize: 14, letterSpacing: 0.8, lineHeight: 1.1 }}>
                SPORTSPIRED <span style={{ color: BRAND_ORANGE }}>GAMES</span>
              </div>
              <div style={{ color: '#8c8c8c', fontSize: 10, letterSpacing: 0.6 }}>IT ASSETS</div>
            </div>
          </div>
          <Drawer
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            placement="left"
            width={260}
            closable={false}
            styles={{ body: { padding: 0, background: BRAND_PURPLE } }}
          >
            {sidebarContent}
          </Drawer>
        </>
      )}

      <Layout style={{ marginLeft: desktopSidebar ? SIDEBAR_WIDTH : 0, minWidth: 0 }}>
        <Content className="app-content" style={{
          padding: desktopSidebar ? 24 : 12,
          paddingTop: desktopSidebar ? 24 : 68,
          background: '#f5f5f5',
          minHeight: '100vh',
          minWidth: 0,
          overflowX: 'hidden',
        }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
